<template>
  <div class="mt-4">
    <v-card class="mx-auto" max-width="95%" elevation="2">
      <v-toolbar dark>
        <v-toolbar-title>프롬북 · 프롬프트 관리</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn small color="primary" depressed class="mr-3" @click="openCreate">
          <v-icon left small>mdi-plus</v-icon>새 프롬프트
        </v-btn>
        <v-chip small outlined dark class="mr-2">
          <v-icon left x-small>mdi-book-open-variant</v-icon>
          총 {{ total.toLocaleString() }}건
        </v-chip>
      </v-toolbar>

      <!-- 필터 영역 -->
      <v-card-text class="pb-0">
        <v-row dense align="center">
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="filter.keyword"
              label="검색어 (제목/본문)"
              outlined dense hide-details clearable
              prepend-inner-icon="mdi-magnify"
              @keyup.enter="onSearch"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="2">
            <v-select
              v-model="filter.is_hidden"
              :items="hiddenOptions"
              label="공개 상태"
              outlined dense hide-details
            ></v-select>
          </v-col>
          <v-col cols="12" sm="2">
            <v-select
              v-model="filter.featured_slot"
              :items="slotOptions"
              label="피처 슬롯"
              outlined dense hide-details
              prepend-inner-icon="mdi-star"
              @change="onSearch"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="2">
            <v-select
              v-model="filter.page_size"
              :items="[50, 100, 200]"
              label="페이지 사이즈"
              outlined dense hide-details
            ></v-select>
          </v-col>
          <v-col cols="12" sm="1">
            <v-btn color="primary" depressed block @click="onSearch">
              <v-icon>mdi-magnify</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- 프롬프트 리스트 -->
      <v-card-text class="pa-0 pt-2">
        <div style="overflow-x: auto;">
          <v-data-table
            :headers="headers"
            :items="list"
            :items-per-page="filter.page_size"
            :footer-props="{'items-per-page-options':[50, 100, 200]}"
            :loading="loading"
            :server-items-length="total"
            class="elevation-0"
          >
            <template v-slot:item.thumb="{ item }">
              <v-avatar size="36" tile v-if="item.thumb">
                <v-img :src="item.thumb"></v-img>
              </v-avatar>
              <v-icon v-else color="grey lighten-1">mdi-image-off</v-icon>
            </template>
            <template v-slot:item.like_count="{ item }">
              <v-chip x-small color="pink lighten-4">{{ item.like_count || 0 }}</v-chip>
            </template>
            <template v-slot:item.report_count="{ item }">
              <v-chip x-small :color="item.report_count > 0 ? 'red lighten-4' : 'grey lighten-3'">
                {{ item.report_count || 0 }}
              </v-chip>
            </template>
            <template v-slot:item.is_main_featured="{ item }">
              <v-btn
                icon small
                :loading="featuringId === item.id"
                @click="toggleFeatured(item)"
                :title="item.is_main_featured ? '이 슬롯 피처 해제' : '이 슬롯에 피처 지정'"
              >
                <v-icon :color="item.is_main_featured ? 'amber darken-2' : 'grey lighten-1'">
                  {{ item.is_main_featured ? 'mdi-star' : 'mdi-star-outline' }}
                </v-icon>
              </v-btn>
            </template>
            <template v-slot:item.is_hidden="{ item }">
              <v-chip x-small :color="item.is_hidden ? 'grey' : 'green'" dark>
                {{ item.is_hidden ? '숨김' : '노출' }}
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn icon small @click="openEditFromList(item)" title="수정" color="blue">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon small @click="openDetail(item)" title="상세보기">
                <v-icon small>mdi-magnify</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </div>
        <v-container v-if="!loading && list.length === 0" class="text-center py-8">
          <v-icon size="56" color="grey lighten-1">mdi-book-search</v-icon>
          <p class="text-body-1 grey--text mt-3">검색 결과가 없습니다.</p>
        </v-container>
      </v-card-text>
    </v-card>

    <!-- 등록/수정 다이얼로그 -->
    <v-dialog v-model="dialogEdit" max-width="780" scrollable persistent>
      <v-card>
        <v-card-title>
          <v-icon left>mdi-pencil-plus</v-icon>
          {{ form.id ? '프롬프트 수정' : '새 프롬프트 등록' }}
          <v-spacer></v-spacer>
          <v-chip v-if="form.id" small outlined>ID {{ form.id }}</v-chip>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-5">
          <v-row dense>
            <!-- 타입 -->
            <v-col cols="12">
              <v-select
                v-model="form.prompt_type"
                :items="promptTypeOptions"
                label="프롬프트 타입"
                outlined dense hide-details
                hint="선택은 로컬에 저장됨 (다음 등록 시 자동 복원)"
                persistent-hint
              ></v-select>
            </v-col>
            <v-col cols="12" v-if="!form.id">
              <v-alert text dense type="info" class="mb-0">
                <v-icon left small>mdi-shield-account</v-icon>
                어드민 등록 — <strong>작성자 = Admin</strong>, <strong>author_type = admin</strong> 으로 고정 저장됨
              </v-alert>
            </v-col>

            <!-- 제목/본문 -->
            <v-col cols="12">
              <v-text-field
                v-model="form.title"
                label="제목 *"
                outlined dense hide-details
                counter="256"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="form.description"
                label="프롬프트 사용법"
                outlined dense
                rows="3"
                auto-grow
                maxlength="1024"
                counter
                hint="프롬프트 사용법 (최대 1024자)"
                persistent-hint
              ></v-textarea>
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="form.body"
                label="프롬프트 본문 *"
                outlined dense hide-details
                rows="8"
                auto-grow
                hint="실제 프롬프트 내용"
                persistent-hint
              ></v-textarea>
            </v-col>

            <!-- 이미지: 파일 업로드 (S3) — 저장 시 자동 업로드 후 URL 이 DB 에 들어감 -->
            <v-col cols="12">
              <v-file-input
                v-model="form.thumb_file"
                label="썸네일 이미지 (작은 이미지 — 리스트용)"
                accept="image/*"
                outlined dense show-size
                prepend-icon=""
                prepend-inner-icon="mdi-image-size-select-small"
                hint="파일을 선택하면 저장 시 S3 에 업로드됩니다. (선택 X = 기존 URL 유지)"
                persistent-hint
              ></v-file-input>
              <v-text-field
                v-if="form.thumb_path"
                v-model="form.thumb_path"
                label="기존 썸네일 URL"
                outlined dense hide-details readonly
                class="mt-2"
                prepend-inner-icon="mdi-link-variant"
              ></v-text-field>
            </v-col>
            <!-- 큰 이미지 (상세용) — 다중, 최대 10장 -->
            <v-col cols="12">
              <div class="d-flex align-center mb-1">
                <span class="text-subtitle-2">큰 이미지 (상세용 · 최대 {{ MAX_IMAGES }}장)</span>
                <v-spacer></v-spacer>
                <v-chip x-small :color="totalImageCount >= MAX_IMAGES ? 'red lighten-4' : 'grey lighten-3'">
                  {{ totalImageCount }} / {{ MAX_IMAGES }}
                </v-chip>
              </div>
              <v-file-input
                v-model="form.image_files"
                label="이미지 추가 (여러 장 선택 가능)"
                accept="image/*"
                multiple small-chips show-size
                outlined dense
                prepend-icon=""
                prepend-inner-icon="mdi-image-multiple"
                hint="저장 시 S3 업로드 후 '기존 → 신규' 순서로 등록됩니다. 첫 장이 대표 이미지(image_path)."
                persistent-hint
                @change="onImageFilesChange"
              ></v-file-input>

              <div v-if="form.images.length || imagePreviews.length" class="d-flex mt-3" style="gap:10px; flex-wrap:wrap">
                <!-- 기존 이미지 -->
                <div v-for="(url, i) in form.images" :key="'ex' + i" style="position:relative; width:110px">
                  <v-img :src="url" width="110" height="110" cover class="grey lighten-3 rounded"></v-img>
                  <v-btn icon x-small color="red" dark style="position:absolute; top:-8px; right:-8px"
                         @click="removeExistingImage(i)">
                    <v-icon x-small>mdi-close-circle</v-icon>
                  </v-btn>
                  <div class="text-center text-caption grey--text">{{ i + 1 }}</div>
                </div>
                <!-- 신규 선택 -->
                <div v-for="(p, i) in imagePreviews" :key="'nw' + i" style="position:relative; width:110px">
                  <v-img :src="p" width="110" height="110" cover class="amber lighten-5 rounded"></v-img>
                  <v-btn icon x-small color="red" dark style="position:absolute; top:-8px; right:-8px"
                         @click="removeNewImage(i)">
                    <v-icon x-small>mdi-close-circle</v-icon>
                  </v-btn>
                  <div class="text-center text-caption amber--text text--darken-2">신규</div>
                </div>
              </div>
            </v-col>
            <!-- 썸네일 미리보기 -->
            <v-col cols="12" v-if="thumbPreview || form.thumb_path">
              <div class="text-caption">썸네일 미리보기</div>
              <v-img :src="thumbPreview || form.thumb_path" max-width="120" max-height="80" contain class="grey lighten-3"></v-img>
            </v-col>

            <!-- 태그 — 한 줄 내려서 여러 줄 입력 가능 -->
            <v-col cols="12">
              <v-textarea
                v-model="tagsText"
                label="태그 (쉼표 또는 줄바꿈으로 구분)"
                outlined dense hide-details
                rows="3"
                auto-grow
                prepend-inner-icon="mdi-tag-multiple-outline"
                hint="예: AI, ChatGPT, 코딩, 한글, 프로그래밍, 디자인…"
                persistent-hint
              ></v-textarea>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn v-if="form.id" text color="red" :loading="deleting" @click="deletePromptFromEdit">
            <v-icon left small>mdi-delete</v-icon>삭제
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogEdit = false">취소</v-btn>
          <v-btn color="primary" depressed :loading="saving" @click="saveForm">
            <v-icon left small>mdi-content-save</v-icon>
            {{ form.id ? '수정' : '등록' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 상세 다이얼로그 -->
    <v-dialog v-model="dialog" max-width="700" scrollable>
      <v-card>
        <v-card-title>
          <v-icon left>mdi-book-open-variant</v-icon>
          프롬프트 상세
          <v-spacer></v-spacer>
          <v-chip small outlined>{{ selected.id }}</v-chip>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <v-simple-table dense>
            <tbody>
              <tr><td class="font-weight-bold" style="width:140px">제목</td><td>{{ selected.title }}</td></tr>
              <tr><td class="font-weight-bold">프롬프트 본문</td><td style="white-space:pre-wrap;max-width:400px">{{ selected.body }}</td></tr>
              <tr><td class="font-weight-bold">프롬프트 사용법</td><td style="white-space:pre-wrap;max-width:400px">{{ selected.description || '-' }}</td></tr>
              <tr><td class="font-weight-bold">태그</td><td>{{ selected.tags }}</td></tr>
              <tr><td class="font-weight-bold">썸네일</td>
                <td>
                  <v-img v-if="selected.thumb_path || selected.thumb" :src="selected.thumb_path || selected.thumb" max-width="120" max-height="80" contain></v-img>
                  <span v-else>-</span>
                </td>
              </tr>
              <tr><td class="font-weight-bold">큰 이미지</td>
                <td>
                  <div v-if="detailImages.length" class="d-flex" style="gap:8px; flex-wrap:wrap">
                    <v-img v-for="(u, i) in detailImages" :key="i" :src="u" width="150" height="150" cover class="grey lighten-3 rounded"></v-img>
                  </div>
                  <span v-else>-</span>
                </td>
              </tr>
              <tr><td class="font-weight-bold">추천수</td><td>{{ selected.like_count }}</td></tr>
              <tr><td class="font-weight-bold">신고수</td><td>{{ selected.report_count }}</td></tr>
              <tr><td class="font-weight-bold">작성자 ID</td><td>{{ selected.author_user_id }}</td></tr>
              <tr><td class="font-weight-bold">작성일</td><td>{{ selected.created_at }}</td></tr>
              <tr><td class="font-weight-bold">공개 상태</td>
                <td>
                  <v-chip x-small :color="selected.is_hidden ? 'grey' : 'green'" dark>
                    {{ selected.is_hidden ? '숨김' : '노출' }}
                  </v-chip>
                </td>
              </tr>
              <tr><td class="font-weight-bold">삭제 여부</td>
                <td>
                  <v-chip x-small :color="selected.is_deleted ? 'red' : 'green'" dark>
                    {{ selected.is_deleted ? '삭제됨' : '정상' }}
                  </v-chip>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn small color="blue" dark @click="openEditFromDetail">
            <v-icon left small>mdi-pencil</v-icon>편집
          </v-btn>
          <v-btn small :color="selected.is_hidden ? 'green' : 'orange'" dark @click="toggleHide">
            <v-icon left small>mdi-eye{{ selected.is_hidden ? '' : '-off' }}</v-icon>
            {{ selected.is_hidden ? '노출' : '숨김' }}
          </v-btn>
          <v-btn small color="red" dark @click="deletePrompt">
            <v-icon left small>mdi-delete</v-icon>삭제
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false">닫기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="py-12"></div>
  </div>
</template>

<script>
import { prombook } from '../../api'
const { FileUpload_Async } = require('../../util/fileutil')

const CLOUDFRONT_PREFIX = 'https://d1r6v24dyewnnj.cloudfront.net/'
const S3_BASE_PATH      = 'project_prombook/prompts'

export default {
  name: 'Prombook_PromptList',
  data() {
    return {
      loading: false,
      total: 0,
      list: [],
      filter: {
        keyword: '',
        is_hidden: null,
        featured_slot: 'main',
        page: 1,
        page_size: 50,
      },
      featuringId: null,            // 피처 토글 진행중인 prompt id (스피너용)
      slotOptions: [
        { text: '메인', value: 'main' },
        { text: '추천', value: 'recommend' },
        { text: '주간', value: 'weekly' },
        { text: '신규이미지', value: 'new_image' },
      ],
      dialog: false,
      selected: {},
      // 등록/수정
      dialogEdit: false,
      deleting: false,        // 수정 다이얼로그 삭제 진행중 플래그
      saving: false,
      form: {
        id: null,
        author_user_id: 'Admin',  // CMS 어드민 등록 — 항상 Admin 고정
        author_type: 'admin',           // 어드민 CMS — 기본 admin 고정
        prompt_type: 'image',           // 기본 이미지 (localStorage 마지막 선택으로 덮어쓰기됨)
        title: '',
        body: '',
        description: '',
        thumb_path: '',                 // 기존 썸네일 URL (편집 시) / 업로드 후 채워짐
        thumb_file: null,               // 신규 업로드용 썸네일 File
        images: [],                     // 큰 이미지 — 기존 URL 배열 (편집 시 유지/삭제)
        image_files: [],                // 큰 이미지 — 신규 업로드용 File 배열
        tags: [],
      },
      MAX_IMAGES: 10,
      thumbPreview: '',                 // 썸네일 로컬 미리보기 (object URL)
      imagePreviews: [],                // 큰 이미지 신규 파일 미리보기 (object URL 배열)
      tagsText: '',
      authorTypeOptions: [
        { text: '관리자 (admin)', value: 'admin' },
        { text: '일반 사용자 (user)', value: 'user' },
      ],
      promptTypeOptions: [
        { text: '이미지 (image)',  value: 'image' },
        { text: '텍스트 (text)',   value: 'text' },
        { text: '기본 (default)',  value: 'default' },
      ],
      // 마지막으로 사용한 prompt_type 을 localStorage 에 저장하는 키
      LAST_TYPE_KEY: 'prombook_cms:last_prompt_type',
      hiddenOptions: [
        { text: '전체', value: null },
        { text: '숨김', value: true },
        { text: '노출', value: false },
      ],
      headers: [
        { text: 'ID', value: 'id', width: '80px' },
        { text: '제목', value: 'title', width: '200px' },
        { text: '썸네일', value: 'thumb', width: '70px', sortable: false },
        { text: '추천수', value: 'like_count', width: '80px' },
        { text: '신고수', value: 'report_count', width: '80px' },
        { text: '작성자', value: 'author_user_id', width: '120px' },
        { text: '작성일', value: 'created_at', width: '150px' },
        { text: '피처', value: 'is_main_featured', width: '70px', sortable: false, align: 'center' },
        { text: '상태', value: 'is_hidden', width: '80px' },
        { text: '액션', value: 'actions', width: '100px', sortable: false },
      ],
    }
  },
  computed: {
    // 등록/수정 폼: 기존 이미지 + 신규 선택 파일 합계 (max 체크/표시용)
    totalImageCount() {
      return (this.form.images ? this.form.images.length : 0) + (this.form.image_files ? this.form.image_files.length : 0)
    },
    // 상세 다이얼로그: 다중 이미지(images) 우선, 없으면 레거시 image_path
    detailImages() {
      const s = this.selected || {}
      if (Array.isArray(s.images) && s.images.length) return s.images
      return s.image_path ? [s.image_path] : []
    },
  },
  methods: {
    async load() {
      try {
        this.loading = true
        const r = await prombook.search_prompt(
          this.filter.keyword,
          this.filter.is_hidden,
          this.filter.page,
          this.filter.page_size,
          this.filter.featured_slot
        )
        this.list = r.prompt_list || []
        this.total = r.total || 0
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    onSearch() {
      this.filter.page = 1
      this.load()
    },
    // 선택한 슬롯(filter.featured_slot)에 대해 피처 지정/해제 토글
    async toggleFeatured(item) {
      const slot = this.filter.featured_slot
      const slotText = (this.slotOptions.find(s => s.value === slot) || {}).text || slot
      try {
        this.featuringId = item.id
        if (item.is_main_featured) {
          await prombook.remove_featured_by_prompt(slot, item.id)
        } else {
          await prombook.add_featured(slot, item.id, 'cms_admin')
        }
        await this.load()
      } catch (e) {
        console.error(e)
        alert(`'${slotText}' 피처 설정 변경에 실패했습니다.`)
      } finally {
        this.featuringId = null
      }
    },
    async openDetail(item) {
      // 리스트 search 응답에는 body, tags 가 없으므로 상세 호출
      try {
        this.loading = true
        const r = await prombook.get_prompt_detail(item.id)
        this.selected = (r && r.prompt_info) ? r.prompt_info : JSON.parse(JSON.stringify(item))
        this.dialog = true
      } catch (e) {
        console.error(e)
        // 폴백 — 리스트 item 만 표시 (body/tags 비어있을 수 있음)
        this.selected = JSON.parse(JSON.stringify(item))
        this.dialog = true
      } finally {
        this.loading = false
      }
    },
    async openEditFromList(item) {
      // 리스트 row 에서 바로 [수정] — body/tags 받아오기 위해 상세 호출
      try {
        this.loading = true
        const r = await prombook.get_prompt_detail(item.id)
        const detail = (r && r.prompt_info) ? r.prompt_info : item
        this.resetPreview()
        this.form = {
          id: detail.id,
          author_user_id: detail.author_user_id || 'Admin',
          author_type: detail.author_type || 'admin',
          prompt_type: detail.prompt_type || 'image',
          title: detail.title || '',
          body: detail.body || '',
          description: detail.description || '',
          thumb_path: detail.thumb_path || '',
          thumb_file: null,
          images: (Array.isArray(detail.images) && detail.images.length) ? detail.images.slice() : (detail.image_path ? [detail.image_path] : []),
          image_files: [],
          tags: Array.isArray(detail.tags) ? detail.tags : (detail.tags ? JSON.parse(detail.tags) : []),
        }
        this.tagsText = this.form.tags.join(', ')
        this.dialogEdit = true
      } catch (e) {
        console.error(e)
        alert('상세 정보를 불러오지 못했습니다.')
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      const lastType = localStorage.getItem(this.LAST_TYPE_KEY) || 'image'
      const validType = ['image', 'text', 'default'].includes(lastType) ? lastType : 'image'
      this.resetPreview()
      this.form = {
        id: null,
        author_user_id: 'Admin',  // CMS 어드민 등록 — 항상 Admin 고정
        author_type: 'admin',     // 어드민 CMS 등록 — admin 고정
        prompt_type: validType,    // 마지막 선택 복원 (없으면 image)
        title: '',
        body: '',
        description: '',
        thumb_path: '',
        thumb_file: null,
        images: [],
        image_files: [],
        tags: [],
      }
      this.tagsText = ''
      this.dialogEdit = true
    },
    resetPreview() {
      if (this.thumbPreview) { try { URL.revokeObjectURL(this.thumbPreview) } catch (e) {} }
      this.thumbPreview = ''
      this.revokeImagePreviews()
    },
    revokeImagePreviews() {
      const arr = this.imagePreviews || []
      arr.forEach(u => { try { URL.revokeObjectURL(u) } catch (e) {} })
      this.imagePreviews = []
    },
    // 큰 이미지 다중 선택 변경 — max 초과 시 잘라내고 미리보기 재생성
    onImageFilesChange() {
      const room = Math.max(0, this.MAX_IMAGES - (this.form.images ? this.form.images.length : 0))
      let files = Array.from(this.form.image_files || [])
      if (files.length > room) {
        alert(`이미지는 최대 ${this.MAX_IMAGES}장까지입니다. (초과분 제외)`)
        files = files.slice(0, room)
        this.form.image_files = files
      }
      this.revokeImagePreviews()
      this.imagePreviews = files.map(f => URL.createObjectURL(f))
    },
    removeExistingImage(i) {
      this.form.images.splice(i, 1)
    },
    removeNewImage(i) {
      this.form.image_files.splice(i, 1)
      this.onImageFilesChange()
    },
    Utill_CreateNewImageName(originalFileName) {
      const ext = originalFileName.substring(originalFileName.lastIndexOf('.'))
      const rand = Math.random().toString(36).substring(2, 18)
      return `${Date.now()}_${rand}${ext}`
    },
    async uploadOneToS3(file, subFolder) {
      // subFolder: 'thumbs' | 'images'
      // 업로드 전 가로 600px 기준으로 비율 유지 리사이즈 (용량 절감)
      const resized = await this.resizeImageToWidth(file, 600)
      const filename = this.Utill_CreateNewImageName(resized.name || file.name)
      const path = `${S3_BASE_PATH}/${subFolder}/${filename}`
      const ok = await FileUpload_Async(resized, path)
      if (!ok) throw new Error('S3 업로드 실패: ' + subFolder)
      return CLOUDFRONT_PREFIX + path
    },
    // 이미지를 가로 maxWidth(px) 기준 비율 유지 리사이즈. 가로가 그 이하면 원본 그대로.
    //   PNG 는 무손실 유지, 그 외 포맷은 JPEG(0.9)로 재인코딩해 용량 절감.
    async resizeImageToWidth(file, maxWidth) {
      try {
        if (!file || !file.type || !file.type.startsWith('image/')) return file
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsDataURL(file)
        })
        const img = await new Promise((resolve, reject) => {
          const image = new Image()
          image.onload = () => resolve(image)
          image.onerror = reject
          image.src = dataUrl
        })
        if (!img.naturalWidth || img.naturalWidth <= maxWidth) return file
        const w = maxWidth
        const h = Math.round(img.naturalHeight * (maxWidth / img.naturalWidth))
        const canvas = document.createElement('canvas')
        canvas.width = w
        canvas.height = h
        canvas.getContext('2d').drawImage(img, 0, 0, w, h)
        const outType = file.type === 'image/png' ? 'image/png' : 'image/jpeg'
        const quality = outType === 'image/jpeg' ? 0.9 : undefined
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, outType, quality))
        if (!blob) return file
        const ext = outType === 'image/png' ? '.png' : '.jpg'
        const baseName = (file.name || 'image').replace(/\.[^.]+$/, '')
        return new File([blob], baseName + ext, { type: outType })
      } catch (e) {
        console.error('이미지 리사이즈 실패, 원본 업로드:', e)
        return file
      }
    },
    openEditFromDetail() {
      this.resetPreview()
      this.form = {
        id: this.selected.id,
        author_user_id: this.selected.author_user_id || '',
        author_type: this.selected.author_type || 'admin',
        prompt_type: this.selected.prompt_type || 'image',
        title: this.selected.title || '',
        body: this.selected.body || '',
        description: this.selected.description || '',
        thumb_path: this.selected.thumb_path || this.selected.thumb || '',
        thumb_file: null,
        images: (Array.isArray(this.selected.images) && this.selected.images.length) ? this.selected.images.slice() : (this.selected.image_path ? [this.selected.image_path] : []),
        image_files: [],
        tags: Array.isArray(this.selected.tags) ? this.selected.tags : [],
      }
      this.tagsText = this.form.tags.join(', ')
      this.dialog = false
      this.dialogEdit = true
    },
    async saveForm() {
      if (!this.form.title || !this.form.body) {
        alert('제목과 본문은 필수입니다.')
        return
      }
      // tagsText → tags 배열 (쉼표 + 줄바꿈 모두 구분자로 인정)
      this.form.tags = (this.tagsText || '')
        .split(/[,\n]/)
        .map(t => t.trim())
        .filter(t => t.length > 0)
      try {
        this.saving = true
        // 선택한 prompt_type 을 localStorage 에 저장 (다음 등록 시 기본값으로 복원)
        try { localStorage.setItem(this.LAST_TYPE_KEY, this.form.prompt_type) } catch (e) { /* ignore */ }

        // 썸네일 업로드
        if (this.form.thumb_file) {
          this.form.thumb_path = await this.uploadOneToS3(this.form.thumb_file, 'thumbs')
        }
        // 큰 이미지(다중) — 신규 선택 파일을 순서대로 S3 업로드 → URL
        const newImageUrls = []
        for (const f of (this.form.image_files || [])) {
          newImageUrls.push(await this.uploadOneToS3(f, 'images'))
        }
        // 최종 images = 기존 유지분 + 신규 (최대 MAX_IMAGES). 백엔드가 image_path = 첫 장으로 호환 처리.
        const finalImages = [...(this.form.images || []), ...newImageUrls].slice(0, this.MAX_IMAGES)

        if (this.form.id) {
          await prombook.update_prompt({
            id: this.form.id,
            prompt_type: this.form.prompt_type,
            title: this.form.title,
            body: this.form.body,
            description: this.form.description,
            thumb_path: this.form.thumb_path,
            images: finalImages,
            tags: this.form.tags,
          })
          alert('수정되었습니다.')
        } else {
          await prombook.create_prompt({
            author_user_id: 'Admin',  // CMS 어드민 등록 — 항상 Admin 고정
            author_type: 'admin',     // 어드민 CMS — 항상 admin
            prompt_type: this.form.prompt_type,
            title: this.form.title,
            body: this.form.body,
            description: this.form.description,
            thumb_path: this.form.thumb_path,
            images: finalImages,
            tags: this.form.tags,
          })
          alert('등록되었습니다.')
        }
        this.dialogEdit = false
        this.load()
      } catch (e) {
        console.error(e)
        alert((this.form.id ? '수정' : '등록') + '에 실패했습니다.')
      } finally {
        this.saving = false
      }
    },
    async toggleHide() {
      if (!confirm(`이 프롬프트를 ${this.selected.is_hidden ? '노출' : '숨김'} 처리하시겠습니까?`)) return
      try {
        await prombook.hide_prompt(this.selected.id, !this.selected.is_hidden)
        alert('처리되었습니다.')
        this.dialog = false
        this.load()
      } catch (e) {
        console.error(e)
        alert('처리에 실패했습니다.')
      }
    },
    async deletePrompt() {
      if (!confirm('이 프롬프트를 삭제하시겠습니까?')) return
      try {
        await prombook.delete_prompt(this.selected.id)
        alert('삭제되었습니다.')
        this.dialog = false
        this.load()
      } catch (e) {
        console.error(e)
        alert('삭제에 실패했습니다.')
      }
    },
    // 수정 다이얼로그에서 삭제 (편집 중인 form.id 기준)
    async deletePromptFromEdit() {
      if (!this.form.id) return
      if (!confirm('진짜로 삭제하시겠습니까?')) return
      try {
        this.deleting = true
        await prombook.delete_prompt(this.form.id)
        alert('삭제되었습니다.')
        this.dialogEdit = false
        this.dialog = false
        this.load()
      } catch (e) {
        console.error(e)
        alert('삭제에 실패했습니다.')
      } finally {
        this.deleting = false
      }
    },
  },
  watch: {
    // 파일 선택 시 즉시 로컬 미리보기 (S3 업로드는 저장 시점)
    'form.thumb_file'(file) {
      if (this.thumbPreview) { try { URL.revokeObjectURL(this.thumbPreview) } catch (e) {} this.thumbPreview = '' }
      if (file) this.thumbPreview = URL.createObjectURL(file)
    },
  },
  beforeDestroy() {
    this.resetPreview()
  },
  mounted() {
    this.load()
  },
}
</script>
